import {
  rsosApi,
  baseQueryArgs,
  baseExtraOptions,
  CURRENT_ORGANIZATION_NAME,
} from '../rsosApi';
import {
  EtlListTransportsResponseSchema,
  EtlCreateTransportResponseSchema,
  EtlTransportTentativeEtaParams,
  type EtlApiTransportRequest,
  type EtlApiTransportUpdateRequest,
  type EtlListTransportsResult,
  type EtlCreateTransportResult,
  type EtlGetTransportTentativeEtaResult,
  EtlGetTransportTentativeEtaResponseSchema,
} from './etlSchemas';

/**
 * We extend @see rsosApi as to let it deal with refresh tokens and retries for
 * us. Transport data gets cached under the api key in emergency data's root reducer.
 */
const enhancedApi = rsosApi.enhanceEndpoints({
  addTagTypes: ['Transports'],
});

/**
 * RTK Query API for etl_integrations service. This includes endpoints for managing GMR/Air Ambulance transports.
 */
const etlIntegrationsApi = enhancedApi.injectEndpoints({
  endpoints: builder => ({
    getTransportTentativeEta: builder.query<
      EtlGetTransportTentativeEtaResult,
      EtlTransportTentativeEtaParams
    >({
      query: params => ({
        ...baseQueryArgs,
        url: 'v1/etl-integrations/api/gmr/transports/eta',
        method: 'GET',
        params: params,
        headers: {
          ...baseQueryArgs.headers,
          // CURRENT_ORGANIZATION_NAME causes rsosApi to fetch use the current org name for us
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse(response: unknown): EtlGetTransportTentativeEtaResult {
        const parsed =
          EtlGetTransportTentativeEtaResponseSchema.parse(response);
        if (parsed.status === 'failure') {
          throw new Error(parsed.detail);
        }

        return parsed.result;
      },
    }),
    getTransports: builder.query<EtlListTransportsResult, void>({
      query: () => ({
        ...baseQueryArgs,
        url: 'v1/etl-integrations/api/gmr/transports',
        method: 'GET',
        headers: {
          ...baseQueryArgs.headers,
          // CURRENT_ORGANIZATION_NAME causes rsosApi to fetch use the current org name for us
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse(response: unknown): EtlListTransportsResult {
        const parsed = EtlListTransportsResponseSchema.parse(response);
        if (parsed.status === 'failure') {
          throw new Error(parsed.detail);
        }
        return parsed.result;
      },
      // providesTags/invalidatesTags implements cache invalidation.
      // invalidatesTags is used on the POST tansports query to invalidate the
      // results of this query, so they're automatically re-fetched.
      providesTags: ['Transports'],
      extraOptions: { ...baseExtraOptions },
    }),
    createTransport: builder.mutation<
      EtlCreateTransportResult,
      EtlApiTransportRequest
    >({
      query: body => ({
        ...baseQueryArgs,
        url: 'v1/etl-integrations/api/gmr/transports',
        method: 'POST',
        body,
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse(response: unknown): EtlCreateTransportResult {
        const parsed = EtlCreateTransportResponseSchema.parse(response);
        if (parsed.status === 'failure') {
          throw new Error(parsed.detail);
        }
        return parsed.result;
      },
      // invalidatesTags invalidates the getTransports query after creating a
      // transport, so that the new transport will be included in the results.
      invalidatesTags: ['Transports'],
      extraOptions: { ...baseExtraOptions },
    }),
    updateTransport: builder.mutation<
      EtlCreateTransportResult,
      { transportId: number; body: EtlApiTransportUpdateRequest }
    >({
      query: ({ transportId, body }) => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/gmr/transports/${transportId}`,
        method: 'PATCH',
        body,
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse(response: unknown): EtlCreateTransportResult {
        const parsed = EtlCreateTransportResponseSchema.parse(response);
        if (parsed.status === 'failure') {
          // NOTE: when throwing in transformResponse the isError and error
          //   properties in the use***Query hooks get updated accordingly per
          //   https://github.com/reduxjs/redux-toolkit/discussions/1920
          throw new Error(parsed.detail);
        }
        return parsed.result;
      },
      invalidatesTags: ['Transports'],
      extraOptions: { ...baseExtraOptions },
    }),
    cancelTransport: builder.mutation<
      void,
      { transportId: number; reason: string }
    >({
      query: ({ transportId, reason }) => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/gmr/transports/${transportId}`,
        method: 'DELETE',
        body: { reason },
        headers: {
          ...baseQueryArgs.headers,
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      invalidatesTags: ['Transports'],
      extraOptions: { ...baseExtraOptions },
    }),
    activateTransport: builder.mutation<
      EtlCreateTransportResult,
      { transportId: number }
    >({
      query: ({ transportId }) => ({
        ...baseQueryArgs,
        url: `v1/etl-integrations/api/gmr/transports/${transportId}/activate`,
        method: 'PATCH',
        headers: {
          ...baseQueryArgs.headers,
          'X-RapidSOS-Org': CURRENT_ORGANIZATION_NAME,
        },
      }),
      transformResponse(response: unknown): EtlCreateTransportResult {
        const parsed = EtlCreateTransportResponseSchema.parse(response);
        if (parsed.status === 'failure') {
          throw new Error(parsed.detail);
        }
        return parsed.result;
      },
      invalidatesTags: ['Transports'],
      extraOptions: { ...baseExtraOptions },
    }),
  }),
  overrideExisting: false,
});

const {
  useLazyGetTransportTentativeEtaQuery,
  useGetTransportsQuery,
  useCreateTransportMutation,
  useUpdateTransportMutation,
  useCancelTransportMutation,
  useActivateTransportMutation,
} = etlIntegrationsApi;

const TRANSPORT_STATUS_POLLING_INTERVAL_MS = 10000;

const useGetTransportsQueryWithPolling = () => {
  if (process.env.NODE_ENV === 'development') {
    const stack = (new Error().stack || '').split('\n');
    const caller = stack.length > 1 ? stack[1] : 'unknown caller';
    // eslint-disable-next-line no-console -- Intentionally verbose logging for development to help identify unnecessary polling
    console.debug('useGetTransportsQueryWithPolling:', caller);
  }
  return useGetTransportsQuery(undefined, {
    pollingInterval: TRANSPORT_STATUS_POLLING_INTERVAL_MS,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  });
};

export {
  useLazyGetTransportTentativeEtaQuery,
  useGetTransportsQuery,
  useGetTransportsQueryWithPolling,
  useCreateTransportMutation,
  useUpdateTransportMutation,
  useCancelTransportMutation,
  useActivateTransportMutation,
};
